import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { apiErr } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = user
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: when returning from the Google OAuth callback, skip /me — AuthCallback exchanges the session_id first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      const { token, ...u } = data;
      setUser(u);
      return { ok: true, role: data.role };
    } catch (e) {
      return { ok: false, error: apiErr(e) };
    }
  }, []);

  const googleSession = useCallback(async (sessionId) => {
    try {
      const { data } = await api.post("/auth/google/session", { session_id: sessionId });
      const { token, new_user, ...u } = data;
      setUser(u);
      return { ok: true, role: data.role };
    } catch (e) {
      return { ok: false, error: apiErr(e) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Server logout failed; clearing local session anyway", e?.message);
    }
    setUser(false);
  }, []);

  const value = useMemo(() => ({ user, loading, login, googleSession, logout, refresh }), [user, loading, login, googleSession, logout, refresh]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
